import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react'
import { RootState } from '../store'
import { cerrarSesion, setTokens } from '../slices/autenticacion/autenticacion.slice'
import { keysToCamel } from '../../utils/ObjetosUtil'
import { AppConstantes } from '../../utils/Constantes'
import { Mutex } from 'async-mutex'
import { setItemsLocalStorage } from '../../utils/StorageUtil'

const mutex = new Mutex()

/**
 * Configura la salida HTTP comun para todos los servicios RTK Query.
 *
 * Usa `window._env_.API_URL_BASE` porque la URL del gateway se inyecta en
 * runtime con `env.sh`. Los endpoints de login y refresh usan Basic Auth del
 * cliente OAuth; el resto usa Bearer token tomado del estado de autenticacion.
 */
const baseQuery = fetchBaseQuery({
  baseUrl: window._env_ && window._env_.API_URL_BASE,
  prepareHeaders: (headers, api) => {
    const { endpoint } = api
    switch (endpoint) {
      case 'iniciarSesion':
      case 'generarNuevoToken':
        headers.set('Authorization', `Basic ${window._env_.CREDENCIALES_CLIENTE}`)
        headers.set('Content-type', 'application/x-www-form-urlencoded')
        break
      case 'endpointSinHeaders':
        break
      default:
        const token = (api.getState() as RootState).autenticacion.accessToken
        if (token) {
          headers.set('Authorization', `Bearer ${token}`)
        }
        break
    }
    return headers
  },
})

/**
 * Ejecuta una llamada base y normaliza respuestas del backend a camelCase.
 *
 * El backend expone contratos en snake_case, mientras que la UI trabaja con
 * modelos TypeScript en camelCase. Esta transformacion evita que cada pantalla
 * replique la misma conversion.
 */
const baseQueryTransform = async (args: any, api: any, extraOptions: any) => {
  const result = await baseQuery(args, api, extraOptions)
  if (result.data) {
    result.data = keysToCamel(result.data)
  }
  if(result.error) {
    result.error = keysToCamel(result.error)
  }
  return result
}

/**
 * Maneja peticiones autenticadas y renueva tokens cuando el backend responde 401.
 *
 * El mutex evita que varias llamadas simultaneas disparen multiples refresh token
 * al mismo tiempo. Si el refresh falla, se limpia la sesion para obligar al
 * usuario a iniciar sesion nuevamente.
 */
const baseQueryRefreshToken = async (args: any, api: any, extraOptions: any) => {
  await mutex.waitForUnlock()
  let result = await baseQuery(args, api, extraOptions)

  if (result.error?.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire()

      try {
        const respRefreshToken = await baseQueryTransform(
          {
            url: '/sec/a/t',
            method: 'POST',
            body: new URLSearchParams({
              refresh_token: (api.getState() as RootState).autenticacion.refreshToken,
              grant_type: 'refresh_token',
            }),
          },
          {
            ...api,
            endpoint: 'generarNuevoToken',
          },
          extraOptions
        )
        if (respRefreshToken && respRefreshToken.data) {
          const { accessToken, refreshToken }: any = respRefreshToken.data
          api.dispatch(setTokens(respRefreshToken.data))
          setItemsLocalStorage([
            { key: AppConstantes.ACCESS_TOKEN, value: accessToken },
            { key: AppConstantes.REFRESH_TOKEN, value: refreshToken },
          ])
          result = await baseQuery(args, api, extraOptions)
        } else {
          api.dispatch(cerrarSesion())
        }
      } finally {
        release()
      }
    } else {
      await mutex.waitForUnlock()
      result = await baseQuery(args, api, extraOptions)
    }
  }

  if (result.data) {
    result.data = keysToCamel(result.data)
  }
  if(result.error) {
    result.error = keysToCamel (result.error)
  }
  return result
}

export const baseApi = createApi({
  baseQuery: baseQueryRefreshToken,
  keepUnusedDataFor: 0,
  endpoints: () => ({}),
})
